import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanManagement = ({ 
  currentPlan = null, 
  planHistory = [], 
  user = null,
  onRegeneratePlan,
  className = ""
}) => {
  const [showHistory, setShowHistory] = useState(false);

  const formatPlanDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPlanStatusColor = (status) => {
    const statusColors = {
      active: "text-success bg-success/10",
      completed: "text-primary bg-primary/10",
      paused: "text-warning bg-warning/10",
      archived: "text-muted-foreground bg-muted"
    };
    return statusColors?.[status] || statusColors?.active;
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Career Plans</h3>
        <div className="flex items-center space-x-2">
          {user?.isPremium && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              iconName={showHistory ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
            >
              History
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            asChild
          >
            <Link to="/ai-plan-generation">New Plan</Link>
          </Button>
        </div>
      </div>
      {/* Current Active Plan */}
      {currentPlan ? (
        <div className="space-y-4">
          <div className="flex items-start justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex-1 space-y-2">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getPlanStatusColor(currentPlan?.status)}`}>
                  <Icon name="Target" size={12} />
                  <span>Active Plan</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  Created {formatPlanDate(currentPlan?.createdAt)}
                </span>
              </div>
              
              <h4 className="font-semibold text-foreground">
                {currentPlan?.occupation} - {currentPlan?.seniority} Level
              </h4>
              
              <p className="text-sm text-muted-foreground line-clamp-2">
                {currentPlan?.summary}
              </p>
              
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Icon name="Target" size={12} />
                  <span>{currentPlan?.totalGoals || 0} Goals</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="CheckCircle" size={12} />
                  <span>{currentPlan?.completedExercises || 0}/{currentPlan?.totalExercises || 0} Exercises</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Calendar" size={12} />
                  <span>Week {currentPlan?.currentWeek || 1}</span>
                </span>
              </div>
            </div>
            
            <div className="flex flex-col space-y-2 ml-4">
              <Button
                variant="default"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                asChild
              >
                <Link to="/ai-plan-generation">View Plan</Link>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onRegeneratePlan}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Regenerate
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Icon name="FileText" size={32} className="text-muted-foreground" />
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">No Active Plan</h4>
            <p className="text-sm text-muted-foreground">
              Create your first AI-powered career development plan to get started
            </p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            asChild
          >
            <Link to="/career-assessment-setup">Create Your Plan</Link>
          </Button>
        </div>
      )}
      {/* Plan History - Premium Feature */}
      {showHistory && user?.isPremium && (
        <div className="space-y-4 border-t border-border pt-4">
          <h4 className="font-medium text-foreground">Plan History</h4>
          
          {planHistory?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-1">
              No previous plans found
            </p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {planHistory?.map((plan, index) => (
                <div key={plan?.id || index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-foreground">
                        {plan?.occupation} - {plan?.seniority}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPlanStatusColor(plan?.status)}`}>
                        {plan?.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatPlanDate(plan?.createdAt)} • {plan?.completedExercises || 0}/{plan?.totalExercises || 0} exercises
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => {/* View archived plan */}}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      onClick={() => {/* Export plan */}}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanManagement;